import { Plus, X } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../utils/cn';
import { Button } from '../Button/Button';

interface AiCommandsListProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  error?: boolean;
}

export const AiCommandsList = ({
  value = [],
  onChange,
  disabled = false,
  error = false,
}: AiCommandsListProps) => {
  const { t } = useTranslation(['knowledge_base']);
  const textareaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const footerRef = useRef<HTMLDivElement>(null);

  const focusTextarea = (index: number, cursorPosition: number) => {
    requestAnimationFrame(() => {
      const el = textareaRefs.current[index];
      if (el) {
        el.focus();
        el.setSelectionRange(cursorPosition, cursorPosition);
      }
    });
  };

  const handleUpdate = (index: number, newText: string) => {
    const newValue = [...value];
    newValue[index] = newText;
    onChange(newValue);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    onChange([...value, '']);
    requestAnimationFrame(() => {
      footerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>, index: number) => {
    const clipboardText = e.clipboardData.getData('text');
    const lines = clipboardText.split('\n').filter((line) => line.trim() !== '');
    if (lines.length <= 1) return;

    e.preventDefault();
    const textarea = e.currentTarget;
    const cursor = textarea.selectionStart;
    const currentText = value[index];
    const textBefore = currentText.substring(0, cursor);
    const textAfter = currentText.substring(textarea.selectionEnd);

    const newEntries: string[] = [];
    newEntries.push(textBefore + lines[0]);
    for (let i = 1; i < lines.length - 1; i++) {
      newEntries.push(lines[i]);
    }
    newEntries.push(lines[lines.length - 1] + textAfter);

    const newValue = [...value];
    newValue.splice(index, 1, ...newEntries);
    onChange(newValue);

    const lastNewIndex = index + newEntries.length - 1;
    const lastLineText = lines[lines.length - 1];
    focusTextarea(lastNewIndex, lastLineText.length);
  };

  return (
    <div
      className={cn(
        'rounded-xl border bg-card transition-colors',
        error ? 'border-destructive' : 'border-border',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <div className="divide-y divide-border">
        {value.map((command, index) => (
          <div key={index} className="flex items-start gap-3 py-1.5 px-1 group">
            <span className="w-5 flex-shrink-0 pt-0.5 text-right text-xs font-medium text-foreground-subtle">
              {index + 1}.
            </span>
            <textarea
              data-testid={`guideline-textarea-${index}`}
              value={command}
              onChange={(e) => {
                handleUpdate(index, e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const textarea = e.currentTarget;
                  const cursor = textarea.selectionStart;
                  const currentText = value[index];
                  const textBefore = currentText.substring(0, cursor);
                  const textAfter = currentText.substring(cursor);
                  const newValue = [...value];
                  newValue[index] = textBefore;
                  newValue.splice(index + 1, 0, textAfter);
                  onChange(newValue);
                  focusTextarea(index + 1, 0);
                }
                if (e.key === 'Backspace' && index > 0) {
                  const textarea = e.currentTarget;
                  const cursor = textarea.selectionStart;
                  if (cursor === 0 && textarea.selectionEnd === 0) {
                    e.preventDefault();
                    const prevText = value[index - 1];
                    const currentText = value[index];
                    const newValue = [...value];
                    newValue[index - 1] = prevText + currentText;
                    newValue.splice(index, 1);
                    onChange(newValue);
                    focusTextarea(index - 1, prevText.length);
                  }
                }
                if (e.key === 'ArrowUp' && index > 0) {
                  e.preventDefault();
                  const cursor = e.currentTarget.selectionStart;
                  const targetLen = value[index - 1].length;
                  focusTextarea(index - 1, Math.min(cursor, targetLen));
                }
                if (e.key === 'ArrowDown' && index < value.length - 1) {
                  e.preventDefault();
                  const cursor = e.currentTarget.selectionStart;
                  const targetLen = value[index + 1].length;
                  focusTextarea(index + 1, Math.min(cursor, targetLen));
                }
              }}
              onPaste={(e) => handlePaste(e, index)}
              placeholder={t('form.commands.placeholder')}
              disabled={disabled}
              rows={1}
              className="mt-px flex-1 resize-none border-none bg-transparent text-sm leading-relaxed text-foreground outline-none placeholder:text-foreground-subtle"
              ref={(el) => {
                textareaRefs.current[index] = el;
                if (el) {
                  el.style.height = 'auto';
                  el.style.height = `${el.scrollHeight}px`;
                }
              }}
            />
            <Button
              type="button"
              onClick={() => handleRemove(index)}
              disabled={disabled}
              variant="custom"
              className="h-auto w-auto flex-shrink-0 rounded-none !border-none bg-transparent p-0 pt-1 pr-1 text-foreground-subtle opacity-0 hover:!bg-transparent hover:text-destructive focus-visible:ring-0 group-hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        ref={footerRef}
        className={cn(
          'flex items-center justify-between bg-accent px-3 py-2',
          value.length > 0 ? 'rounded-b-xl border-t border-border' : 'rounded-xl',
        )}
      >
        <Button type="button" onClick={handleAdd} disabled={disabled} variant="secondary" size="xs">
          <Plus className="h-3.5 w-3.5" />
          {t('form.commands.add')}
        </Button>
        <span
          className={cn(
            'text-xs',
            value.join('').length > 4500 ? 'text-warning' : 'text-foreground-subtle',
          )}
        >
          {value.join('').length} / 5000
        </span>
      </div>
    </div>
  );
};

export default AiCommandsList;
